import React, { useState } from 'react';
import { App as AntApp, Button, Flex } from 'antd';
import { CreditCardOutlined, LogoutOutlined } from '@ant-design/icons';
import type { AccessReason } from '@lingoping/core';
import { Echo, type EchoMood } from '../Echo.js';
import { AccountActions } from './AccountActions.js';
import { PriceComparison } from './PriceComparison.js';
import { BRAND } from './brand.js';
import { openBillingPortalUrl } from './billingApi.js';

const COPY: Record<
  Extract<AccessReason, 'trial_ended' | 'payment_failed' | 'subscription_ended'>,
  { title: string; line?: string; mood: EchoMood }
> = {
  trial_ended: {
    title: 'Your free trial is over.',
    line: 'Your cards are safe.',
    mood: 'sleeping',
  },
  payment_failed: {
    title: "Your last payment didn't go through.",
    line: 'Your cards are safe.',
    mood: 'sad',
  },
  subscription_ended: {
    title: 'Your subscription has ended.',
    line: 'Your cards are safe.',
    mood: 'sleeping',
  },
};

interface Props {
  reason: AccessReason;
  onSignOut: () => Promise<void>;
}

/** Shown instead of every tab while the learner is locked out (Settings > Account stays open). */
export const LockScreen: React.FC<Props> = ({ reason, onSignOut }) => {
  const { message } = AntApp.useApp();
  const [portalLoading, setPortalLoading] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const copy = reason in COPY ? COPY[reason as keyof typeof COPY] : COPY.trial_ended;
  const paymentFailed = reason === 'payment_failed';

  const changeCard = async () => {
    setPortalLoading(true);
    try {
      window.location.assign(await openBillingPortalUrl());
    } catch (err) {
      console.error('Could not open the billing portal:', err);
      message.error("The card page couldn't be opened. Please try again.");
      setPortalLoading(false);
    }
  };

  return (
    <Flex vertical gap={28} style={{ maxWidth: 820, margin: '0 auto' }}>
      <Flex
        vertical
        align="center"
        gap={10}
        role="status"
        style={{
          textAlign: 'center',
          padding: '28px 16px',
          background: '#fff',
          border: `2px solid ${BRAND.line}`,
          borderRadius: 16,
        }}
      >
        <Echo mood={copy.mood} size={120} />
        <h1 style={{ margin: 0, fontSize: 26, lineHeight: 1.25, color: BRAND.deep }}>
          {copy.title}
        </h1>
        {copy.line && <div style={{ fontSize: 16, color: '#475569' }}>{copy.line}</div>}
        <Flex gap={8} wrap justify="center" style={{ marginTop: 8 }}>
          {paymentFailed ? (
            <Button
              type="primary"
              size="large"
              icon={<CreditCardOutlined />}
              loading={portalLoading}
              onClick={() => void changeCard()}
              style={{ background: BRAND.main }}
            >
              Change card
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              href="#subscribe"
              style={{ background: BRAND.main }}
            >
              Subscribe
            </Button>
          )}
        </Flex>
      </Flex>

      {!paymentFailed && <PriceComparison compact />}

      <Flex vertical align="center" gap={16}>
        <AccountActions>
          <Button
            icon={<LogoutOutlined />}
            loading={signingOut}
            onClick={async () => {
              setSigningOut(true);
              try {
                await onSignOut();
              } finally {
                setSigningOut(false);
              }
            }}
          >
            Sign out
          </Button>
        </AccountActions>
      </Flex>
    </Flex>
  );
};
